/**
 * AddMissingComponentDialog Component
 * 
 * Allows users to investigate why a component wasn't extracted and create rules to fix it.
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Rule as RuleIcon,
  AutoFixHigh as MagicIcon,
} from '@mui/icons-material';
import { investigateMissingComponent, createForceIncludeRule, generateRuleWithLLM, applyRuleIncremental, InvestigationResult } from '../../services/bomApi';
import { ExtractionRule } from '../../types/bom.types';

interface AddMissingComponentDialogProps {
  open: boolean;
  onClose: () => void;
  filename?: string;
  jobId?: string;  // For incremental rule application
  pdfPath?: string;  // For scanning PDF with new rules
  folderId?: string;  // Customer folder for rule scoping
  onRuleCreated?: (newItems?: Array<{ reference: string; page: number; component_type: string }>) => void;
  extractionExamples?: Array<{
    reference: string;
    component_type?: string;
    description?: string;
    context?: string;
  }>;
}

const AddMissingComponentDialog: React.FC<AddMissingComponentDialogProps> = ({
  open,
  onClose,
  filename,
  jobId,
  pdfPath,
  folderId,
  onRuleCreated,
  extractionExamples = [],
}) => {
  const [reference, setReference] = useState('');
  const [investigating, setInvestigating] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generatingWithLLM, setGeneratingWithLLM] = useState(false);
  const [result, setResult] = useState<InvestigationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showDetails, setShowDetails] = useState(false);
  const [customComponentType, setCustomComponentType] = useState('');
  const [ruleCreated, setRuleCreated] = useState(false);
  const [itemsAddedCount, setItemsAddedCount] = useState(0);
  const [llmRule, setLlmRule] = useState<{
    success: boolean;
    rule: ExtractionRule;
    pattern: string;
    reasoning: string;
    investigation: InvestigationResult;
    message: string;
  } | null>(null);

  const handleInvestigate = useCallback(async () => {
    if (!reference.trim()) {
      setError('Please enter a component reference');
      return;
    }

    setInvestigating(true);
    setError(null);
    setResult(null);
    setRuleCreated(false);
    setItemsAddedCount(0);

    try {
      const response = await investigateMissingComponent(reference.trim(), filename);
      setResult(response);
      setActiveStep(1);
      
      // Pre-fill component type from suggested rule
      if (response.suggested_rule?.component_type) {
        setCustomComponentType(response.suggested_rule.component_type);
      }
    } catch (err: any) {
      setError(err.message || 'Investigation failed');
    } finally {
      setInvestigating(false);
    }
  }, [reference, filename]);

  const handleCreateRule = useCallback(async () => {
    if (!result) return;

    setCreating(true);
    setError(null);

    try {
      // Step 1: Create the rule - scope to folder if available, otherwise global
      const response = await createForceIncludeRule({
        reference: reference.trim(),
        component_type: customComponentType || result.suggested_rule?.component_type || 'OTHER',
        description: `Force-include ${reference} - created from Training UI`,
        scope: folderId ? 'folder' : 'global',
        scope_value: folderId,
      });

      if (response.success && response.rule) {
        // Step 2: If we have a job_id, apply the rule incrementally (preserves existing verification)
        if (jobId && response.rule.id) {
          try {
            const applyResult = await applyRuleIncremental({
              rule_id: response.rule.id,
              job_id: jobId,
              pdf_path: pdfPath || filename,
            });
            
            if (applyResult.success) {
              console.log(`✅ Rule applied incrementally: ${applyResult.items_added} items added, ${applyResult.items_unchanged} unchanged`);
              setRuleCreated(true);
              setItemsAddedCount(applyResult.items_added || 0);
              setActiveStep(2);
              // Pass new items back to parent so they can be added to the UI
              onRuleCreated?.(applyResult.new_items);
            } else {
              // Rule created but incremental apply failed - user will need to re-extract
              console.warn('Rule created but incremental apply failed');
              setRuleCreated(true);
              setActiveStep(2);
              onRuleCreated?.();
            }
          } catch (applyErr) {
            // Rule created but incremental apply failed - user will need to re-extract
            console.warn('Rule created but incremental apply failed:', applyErr);
            setRuleCreated(true);
            setActiveStep(2);
            onRuleCreated?.();
          }
        } else {
          // No job_id - traditional flow (re-extract required)
          setRuleCreated(true);
          setActiveStep(2);
          onRuleCreated?.();
        }
      } else {
        setError('Failed to create rule');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create rule');
    } finally {
      setCreating(false);
    }
  }, [result, reference, customComponentType, onRuleCreated, jobId, pdfPath, filename]);

  const handleGenerateWithLLM = useCallback(async () => {
    if (!reference.trim()) {
      setError('Please enter a component reference');
      return;
    }

    setGeneratingWithLLM(true);
    setError(null);
    setLlmRule(null);

    try {
      const response = await generateRuleWithLLM({
        reference: reference.trim(),
        filename: filename,
        extraction_examples: extractionExamples,
      });

      if (response.success) {
        setLlmRule(response);
        setResult(response.investigation);
        setActiveStep(2); // Skip to rule created step
        setRuleCreated(true);
        
        // If we have a job_id, apply the rule incrementally (preserves existing verification)
        if (jobId && response.rule?.id) {
          try {
            const applyResult = await applyRuleIncremental({
              rule_id: response.rule.id,
              job_id: jobId,
              pdf_path: pdfPath || filename,
            });
            
            if (applyResult.success) {
              console.log(`✅ LLM Rule applied incrementally: ${applyResult.items_added} items added`);
              setItemsAddedCount(applyResult.items_added || 0);
              onRuleCreated?.(applyResult.new_items);
            } else {
              setItemsAddedCount(0);
              onRuleCreated?.();
            }
          } catch (applyErr) {
            console.warn('LLM Rule created but incremental apply failed:', applyErr);
            setItemsAddedCount(0);
            onRuleCreated?.();
          }
        } else {
          setItemsAddedCount(0);
          onRuleCreated?.();
        }
      } else {
        setError('Failed to generate rule with LLM');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to generate rule with LLM. Make sure LLM_PATTERN_GENERATION is enabled.');
    } finally {
      setGeneratingWithLLM(false);
    }
  }, [reference, filename, extractionExamples, onRuleCreated, jobId, pdfPath]);

  const handleClose = () => {
    setReference('');
    setResult(null);
    setError(null);
    setActiveStep(0);
    setShowDetails(false);
    setRuleCreated(false);
    setCustomComponentType('');
    setLlmRule(null);
    onClose();
  };

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'NOT_IN_PDF':
        return 'error';
      case 'NOT_EXTRACTED':
      case 'FILTERED_OUT':
        return 'warning';
      case 'SHOULD_BE_EXTRACTED':
        return 'success';
      default:
        return 'info';
    }
  };

  const getRecommendationIcon = (recommendation: string) => {
    switch (recommendation) {
      case 'NOT_IN_PDF':
        return <CancelIcon color="error" />;
      case 'NOT_EXTRACTED':
      case 'FILTERED_OUT':
        return <WarningIcon color="warning" />;
      case 'SHOULD_BE_EXTRACTED':
        return <CheckIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MagicIcon color="primary" />
        Add Missing Component
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {/* Step 1: Enter Reference */}
            <Step>
              <StepLabel>
                <Typography variant="subtitle1">Enter Component Reference</Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Component Reference"
                    placeholder="e.g., RCD12, -XL1, XEN2"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleInvestigate()}
                    helperText="Enter the reference as it appears in the PDF"
                    disabled={investigating}
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    onClick={handleInvestigate}
                    disabled={investigating || !reference.trim()}
                    startIcon={investigating ? <CircularProgress size={16} /> : <SearchIcon />}
                    sx={{ minWidth: 120, mt: 1 }}
                  >
                    {investigating ? 'Checking...' : 'Investigate'}
                  </Button>
                </Box>
                
                {filename && (
                  <Typography variant="caption" color="text.secondary">
                    Investigating in: <strong>{filename}</strong>
                  </Typography>
                )}
              </StepContent>
            </Step>

            {/* Step 2: Review Results */}
            <Step>
              <StepLabel>
                <Typography variant="subtitle1">Review Investigation Results</Typography>
              </StepLabel>
              <StepContent>
                {result && (
                  <Box sx={{ mb: 2 }}>
                    {/* Main Recommendation */}
                    <Alert 
                      severity={getRecommendationColor(result.recommendation) as any}
                      icon={getRecommendationIcon(result.recommendation)}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {result.recommendation_text}
                      </Typography>
                    </Alert>

                    {/* PDF Check */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.found_in_pdf ? (
                          <CheckIcon color="success" />
                        ) : (
                          <CancelIcon color="error" />
                        )}
                        <Typography variant="subtitle2">
                          {result.found_in_pdf 
                            ? `Found "${reference}" in PDF` 
                            : `"${reference}" NOT found in PDF`}
                        </Typography>
                      </Box>
                      
                      {result.pdf_contexts.length > 0 && (
                        <Box sx={{ mt: 1, pl: 4 }}>
                          {result.pdf_contexts.map((ctx, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Chip 
                                size="small" 
                                label={`Page ${ctx.page}`} 
                                sx={{ mr: 1 }} 
                              />
                              <Typography 
                                variant="caption" 
                                component="span"
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  bgcolor: 'action.hover',
                                  p: 0.5,
                                  borderRadius: 1
                                }}
                              >
                                {ctx.context}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Paper>

                    {/* Filter Details (Expandable) */}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        size="small"
                        onClick={() => setShowDetails(!showDetails)}
                        startIcon={showDetails ? <CollapseIcon /> : <ExpandIcon />}
                      >
                        {showDetails ? 'Hide' : 'Show'} Filter Details
                      </Button>
                      
                      <Collapse in={showDetails}>
                        <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Filter Check Results:
                          </Typography>
                          <List dense>
                            {result.filter_results.map((filter, idx) => (
                              <ListItem key={idx}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  {filter.passed ? (
                                    <CheckIcon color="success" fontSize="small" />
                                  ) : (
                                    <CancelIcon color="error" fontSize="small" />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={filter.filter}
                                  secondary={filter.detail}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Collapse>
                    </Box>

                    {/* Suggested Rule */}
                    {result.suggested_rule && result.recommendation !== 'NOT_IN_PDF' && (
                      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <RuleIcon />
                          <Typography variant="subtitle2">
                            Suggested Fix: Create Force-Include Rule
                          </Typography>
                        </Box>
                        
                        <Box sx={{ pl: 4 }}>
                          <Typography variant="body2">
                            <strong>Pattern:</strong> <code>{result.suggested_rule.pattern}</code>
                          </Typography>
                          <Typography variant="body2">
                            <strong>Description:</strong> {result.suggested_rule.description}
                          </Typography>
                          
                          <FormControl size="small" sx={{ mt: 2, minWidth: 200 }}>
                            <InputLabel sx={{ color: 'inherit' }}>Component Type</InputLabel>
                            <Select
                              value={customComponentType || result.suggested_rule.component_type}
                              onChange={(e) => setCustomComponentType(e.target.value)}
                              label="Component Type"
                              sx={{ bgcolor: 'background.paper' }}
                            >
                              <MenuItem value="RCD">RCD (Residual Current Device)</MenuItem>
                              <MenuItem value="MCB">MCB (Circuit Breaker)</MenuItem>
                              <MenuItem value="TERMINAL">TERMINAL</MenuItem>
                              <MenuItem value="ENCODER">ENCODER</MenuItem>
                              <MenuItem value="RELAY">RELAY</MenuItem>
                              <MenuItem value="CONTACTOR">CONTACTOR</MenuItem>
                              <MenuItem value="SWITCH">SWITCH</MenuItem>
                              <MenuItem value="OTHER">OTHER</MenuItem>
                              {result.suggested_rule.component_type && 
                                !['RCD', 'MCB', 'TERMINAL', 'ENCODER', 'RELAY', 'CONTACTOR', 'SWITCH', 'OTHER'].includes(result.suggested_rule.component_type) && (
                                <MenuItem value={result.suggested_rule.component_type}>
                                  {result.suggested_rule.component_type}
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </Box>
                      </Paper>
                    )}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  <Button onClick={() => setActiveStep(0)}>
                    Back
                  </Button>
                  {result?.recommendation !== 'NOT_IN_PDF' && result?.recommendation !== 'SHOULD_BE_EXTRACTED' && (
                    <>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleGenerateWithLLM}
                        disabled={generatingWithLLM}
                        startIcon={generatingWithLLM ? <CircularProgress size={16} /> : <MagicIcon />}
                        sx={{ flex: 1 }}
                      >
                        {generatingWithLLM ? 'AI Generating...' : '🤖 Generate Rule with AI'}
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCreateRule}
                        disabled={creating}
                        startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
                        sx={{ flex: 1 }}
                      >
                        {creating ? 'Creating...' : 'Create Rule (Manual)'}
                      </Button>
                    </>
                  )}
                </Box>
                
                {/* Show LLM-generated rule if available */}
                {llmRule && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'success.light', color: 'success.contrastText' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MagicIcon />
                      <Typography variant="subtitle2">
                        AI-Generated Rule
                      </Typography>
                    </Box>
                    <Box sx={{ pl: 4 }}>
                      <Typography variant="body2">
                        <strong>Pattern:</strong>{' '}
                        <code style={{ 
                          backgroundColor: 'rgba(0,0,0,0.1)', 
                          padding: '2px 4px', 
                          borderRadius: '2px' 
                        } as React.CSSProperties}>
                          {llmRule.pattern}
                        </code>
                      </Typography>
                      <Typography variant="body2">
                        <strong>Component Type:</strong> {llmRule.rule.component_type}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Action:</strong> {llmRule.rule.action}
                      </Typography>
                      {llmRule.reasoning && (
                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                          <strong>AI Reasoning:</strong> {llmRule.reasoning}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                )}
              </StepContent>
            </Step>

            {/* Step 3: Success */}
            <Step>
              <StepLabel>
                <Typography variant="subtitle1">Rule Created</Typography>
              </StepLabel>
              <StepContent>
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    ✅ Rule created for "{reference}"
                  </Typography>
                  <Typography variant="body2">
                    {itemsAddedCount > 0
                      ? `${itemsAddedCount} item(s) added to extraction.`
                      : llmRule 
                        ? `AI-generated pattern: ${llmRule.pattern}`
                        : 'No matching items found in the current extraction.'}
                  </Typography>
                </Alert>
                
                {llmRule && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Generated Rule Details:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Pattern:</strong> <code>{llmRule.pattern}</code>
                    </Typography>
                    <Typography variant="body2">
                      <strong>Component Type:</strong> {llmRule.rule.component_type}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Action:</strong> {llmRule.rule.action}
                    </Typography>
                    {llmRule.reasoning && (
                      <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        <strong>AI Reasoning:</strong> {llmRule.reasoning}
                      </Typography>
                    )}
                  </Paper>
                )}
                
                <Button variant="contained" onClick={handleClose}>
                  Done
                </Button>
              </StepContent>
            </Step>
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {ruleCreated ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMissingComponentDialog;

